import React, { useRef, useEffect, useState, useCallback } from 'react';
import { User } from '../../services/authService';
import { getDrawing, saveDrawing, updateDrawing, Drawing as DrawingType } from '../../services/drawingService';
import { generateImageBase64 } from '../../services/imageService';
import PageHeader from '../common/PageHeader';
import PastDrawings from './PastDrawings';
import './Drawing.css';

interface DrawingProps {
  user: User | null;
  onToggleSidebar: () => void;
  onClose: () => void;
}

const Drawing: React.FC<DrawingProps> = ({ user, onToggleSidebar }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [brushSize, setBrushSize] = useState(4);
  const [eraserSize, setEraserSize] = useState(8);
  const [tool, setTool] = useState<'brush' | 'eraser'>('brush');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<number>(0);
  const [showPastDrawings, setShowPastDrawings] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [backgroundOpacity, setBackgroundOpacity] = useState<number>(0.3);
  const [showBackground, setShowBackground] = useState<boolean>(true);
  const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false);
  const [generatePrompt, setGeneratePrompt] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [currentDrawingId, setCurrentDrawingId] = useState<number | null>(null);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get user ID helper
  const getUserId = (): number => {
    return user?.id || 0;
  };

  // Load previous drawing on mount
  useEffect(() => {
    const loadPreviousDrawing = async () => {
      const userId = getUserId();
      if (!userId) return;

      setIsLoading(true);
      try {
        const drawing = await getDrawing(userId);
        if (drawing && canvasRef.current) {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          // Store the current drawing ID
          setCurrentDrawingId(drawing.id);

          // Restore brush and eraser sizes
          setBrushSize(drawing.brush_size || 4);
          setEraserSize(drawing.eraser_size || 8);

          // Restore background image and opacity
          if (drawing.background_image) {
            setBackgroundImage(drawing.background_image);
            setBackgroundOpacity(drawing.background_opacity ?? 0.3);
            setShowBackground(true);
          } else {
            setBackgroundImage(null);
            setShowBackground(false);
          }

          // Load canvas image from base64
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            setHasUnsavedChanges(false);
          };
          img.src = drawing.canvas_data;
        }
      } catch (error) {
        console.error('Failed to load drawing:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreviousDrawing();
  }, [user]);

  // Initialize canvas size (only on mount and resize)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (container) {
        // Save current canvas content before resizing
        const imageData = canvas.toDataURL('image/png');
        const img = new Image();
        
        const oldWidth = canvas.width;
        const newWidth = container.clientWidth;
        const oldHeight = canvas.height;
        const newHeight = container.clientHeight - 1;
        
        // Only resize if dimensions actually changed
        if (oldWidth !== newWidth || oldHeight !== newHeight) {
          canvas.width = newWidth;
          canvas.height = newHeight;
          
          // Restore canvas content after resize
          if (imageData && imageData !== 'data:,') {
            img.onload = () => {
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0);
              }
            };
            img.src = imageData;
          }
        }
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []); // Only run on mount

  // Update drawing context properties when tool or size changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Configure drawing context (without resizing)
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = tool === 'brush' ? brushSize : eraserSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over';
  }, [tool, brushSize, eraserSize]);

  const handleAutoSave = useCallback(async () => {
    const userId = getUserId();
    if (!userId || !canvasRef.current) return;

    try {
      const canvas = canvasRef.current;
      const canvasData = canvas.toDataURL('image/png');
      
      // If we have a current drawing ID, update it. Otherwise, create a new one.
      if (currentDrawingId !== null) {
        await updateDrawing(userId, canvasData, brushSize, eraserSize, backgroundImage, backgroundOpacity);
      } else {
        const savedDrawing = await saveDrawing(userId, canvasData, brushSize, eraserSize, backgroundImage, backgroundOpacity);
        setCurrentDrawingId(savedDrawing.id);
      }
      setHasUnsavedChanges(false);
      setLastSaveTime(Date.now());
    } catch (error) {
      console.error('Failed to auto-save drawing:', error);
    }
  }, [currentDrawingId, brushSize, eraserSize, backgroundImage, backgroundOpacity]);

  // Periodic auto-save
  useEffect(() => {
    if (hasUnsavedChanges && getUserId()) {
      autoSaveIntervalRef.current = setInterval(() => {
        handleAutoSave();
      }, 30000); // Auto-save every 30 seconds
    }

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [hasUnsavedChanges, handleAutoSave]);

  // Save on unmount
  useEffect(() => {
    return () => {
      const userId = getUserId();
      if (hasUnsavedChanges && userId && canvasRef.current) {
        const canvas = canvasRef.current;
        const canvasData = canvas.toDataURL('image/png');
        // Fire and forget - don't await
        if (currentDrawingId !== null) {
          updateDrawing(userId, canvasData, brushSize, eraserSize, backgroundImage, backgroundOpacity).catch(console.error);
        } else {
          saveDrawing(userId, canvasData, brushSize, eraserSize, backgroundImage, backgroundOpacity).catch(console.error);
        }
      }
    };
  }, [hasUnsavedChanges, currentDrawingId, brushSize, eraserSize, backgroundImage, backgroundOpacity]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement> | TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    if ('touches' in e && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    } else {
      return {
        x: (e as React.MouseEvent<HTMLCanvasElement>).clientX - rect.left,
        y: (e as React.MouseEvent<HTMLCanvasElement>).clientY - rect.top,
      };
    }
  };

  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement> | TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Set tool-specific properties
    ctx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.lineWidth = tool === 'brush' ? brushSize : eraserSize;
    ctx.strokeStyle = '#333333';

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  }, [tool, brushSize, eraserSize]);

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement> | TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasUnsavedChanges(true);
  }, [isDrawing]);

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Add touch event listeners with passive: false to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e: TouchEvent) => {
      startDrawing(e);
    };

    const handleTouchMove = (e: TouchEvent) => {
      draw(e);
    };

    const handleTouchEnd = () => {
      stopDrawing();
    };

    // Add touch listeners with passive: false to allow preventDefault
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [startDrawing, draw]);

  const handleNewCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    if (window.confirm('Are you sure you want to clear the canvas? This cannot be undone.')) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasUnsavedChanges(true);
    }
    setShowMenu(false);
  };

  const handleNewDrawing = async () => {
    const userId = getUserId();
    if (!userId || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Save current drawing if there are unsaved changes
    if (hasUnsavedChanges && currentDrawingId !== null) {
      try {
        const canvasData = canvas.toDataURL('image/png');
        await updateDrawing(userId, canvasData, brushSize, eraserSize, backgroundImage, backgroundOpacity);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to save drawing before creating new one:', error);
        alert('Failed to save current drawing. Please try again.');
        setShowMenu(false);
        return;
      }
    }

    // Clear canvas for new drawing and reset drawing ID
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setBackgroundImage(null);
    setShowBackground(false);
    setCurrentDrawingId(null); // Clear ID so next save creates a new drawing
    setHasUnsavedChanges(false);
    setShowMenu(false);
  };

  const handleLoadPrevious = async () => {
    const userId = getUserId();
    if (!userId) return;

    setIsLoading(true);
      try {
        const drawing = await getDrawing(userId);
        if (drawing && canvasRef.current) {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          // Store the current drawing ID
          setCurrentDrawingId(drawing.id);

          // Restore brush and eraser sizes
          setBrushSize(drawing.brush_size || 4);
          setEraserSize(drawing.eraser_size || 8);

          // Restore background image and opacity
          if (drawing.background_image) {
            setBackgroundImage(drawing.background_image);
            setBackgroundOpacity(drawing.background_opacity ?? 0.3);
            setShowBackground(true);
          } else {
            setBackgroundImage(null);
            setShowBackground(false);
          }

          // Load canvas image from base64
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            setHasUnsavedChanges(false);
          };
          img.src = drawing.canvas_data;
        } else {
          alert('No previous drawing found.');
        }
    } catch (error) {
      console.error('Failed to load drawing:', error);
      alert('Failed to load previous drawing.');
    } finally {
      setIsLoading(false);
      setShowMenu(false);
    }
  };

  const handleLoadDrawing = async (drawing: DrawingType) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsLoading(true);
    try {
      // Store the current drawing ID
      setCurrentDrawingId(drawing.id);

      // Restore brush and eraser sizes
      setBrushSize(drawing.brush_size || 4);
      setEraserSize(drawing.eraser_size || 8);

      // Restore background image and opacity
      if (drawing.background_image) {
        setBackgroundImage(drawing.background_image);
        setBackgroundOpacity(drawing.background_opacity ?? 0.3);
        setShowBackground(true);
      } else {
        setBackgroundImage(null);
        setShowBackground(false);
      }

      // Load canvas image from base64
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        setHasUnsavedChanges(false);
      };
      img.onerror = () => {
        alert('Failed to load drawing image.');
        setIsLoading(false);
      };
      img.src = drawing.canvas_data;
    } catch (error) {
      console.error('Failed to load drawing:', error);
      alert('Failed to load drawing.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateSketch = () => {
    setShowGenerateModal(true);
    setShowMenu(false);
  };

  const handleConfirmGenerate = async () => {
    if (!generatePrompt.trim()) {
      alert('Please enter a description for the sketch.');
      return;
    }

    const userId = getUserId();
    if (!userId || !canvasRef.current) return;

    setIsGenerating(true);
    try {
      // Enhance prompt for sketch generation
      const enhancedPrompt = `Create a simple line art sketch of ${generatePrompt}. Use clean lines, minimal details, suitable for tracing practice. Do not include text or words in the image.`;
      
      // Generate image using Gemini
      const result = await generateImageBase64(enhancedPrompt);
      
      // Convert base64 data to data URI format if needed
      const imageDataUri = result.image.startsWith('data:') 
        ? result.image 
        : `data:${result.mimeType};base64,${result.image}`;
      
      // Replace existing background (as per requirement 4a)
      setBackgroundImage(imageDataUri);
      setBackgroundOpacity(0.3); // Default opacity
      setShowBackground(true);
      
      // Save the drawing with new background
      const canvas = canvasRef.current;
      const canvasData = canvas.toDataURL('image/png');
      if (currentDrawingId !== null) {
        const updatedDrawing = await updateDrawing(userId, canvasData, brushSize, eraserSize, imageDataUri, 0.3);
        setCurrentDrawingId(updatedDrawing.id);
      } else {
        const savedDrawing = await saveDrawing(userId, canvasData, brushSize, eraserSize, imageDataUri, 0.3);
        setCurrentDrawingId(savedDrawing.id);
      }
      
      // Close modal and reset prompt
      setShowGenerateModal(false);
      setGeneratePrompt('');
    } catch (error) {
      console.error('Failed to generate sketch:', error);
      alert('Failed to generate sketch. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBackgroundOpacityChange = async (opacity: number) => {
    setBackgroundOpacity(opacity);
    
    // Save opacity change
    const userId = getUserId();
    if (userId && canvasRef.current) {
      try {
        const canvas = canvasRef.current;
        const canvasData = canvas.toDataURL('image/png');
        if (currentDrawingId !== null) {
          await updateDrawing(userId, canvasData, brushSize, eraserSize, backgroundImage, opacity);
        } else {
          const savedDrawing = await saveDrawing(userId, canvasData, brushSize, eraserSize, backgroundImage, opacity);
          setCurrentDrawingId(savedDrawing.id);
        }
      } catch (error) {
        console.error('Failed to save opacity change:', error);
      }
    }
  };

  const handleToggleBackground = () => {
    setShowBackground(!showBackground);
  };

  const handleRemoveBackground = async () => {
    if (window.confirm('Are you sure you want to remove the background image?')) {
      setBackgroundImage(null);
      setShowBackground(false);
      
      // Save removal
      const userId = getUserId();
      if (userId && canvasRef.current) {
        try {
          const canvas = canvasRef.current;
          const canvasData = canvas.toDataURL('image/png');
          if (currentDrawingId !== null) {
            await updateDrawing(userId, canvasData, brushSize, eraserSize, null, null);
          } else {
            const savedDrawing = await saveDrawing(userId, canvasData, brushSize, eraserSize, null, null);
            setCurrentDrawingId(savedDrawing.id);
          }
        } catch (error) {
          console.error('Failed to save background removal:', error);
        }
      }
    }
  };

  const rightActions = (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        className="drawing-menu-btn"
        onClick={() => setShowMenu(!showMenu)}
        title="Menu"
      >
        ⋯
      </button>
      {showMenu && (
        <div className="drawing-menu-dropdown">
          <button
            className="drawing-menu-item"
            onClick={handleNewDrawing}
            disabled={isLoading}
          >
            New Drawing
          </button>
          <button
            className="drawing-menu-item"
            onClick={handleGenerateSketch}
            disabled={isGenerating}
          >
            Generate Sketch
          </button>
          <button
            className="drawing-menu-item"
            onClick={() => {
              setShowPastDrawings(true);
              setShowMenu(false);
            }}
          >
            Past Drawings
          </button>
          <button
            className="drawing-menu-item"
            onClick={handleLoadPrevious}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Load Previous'}
          </button>
          <button
            className="drawing-menu-item"
            onClick={handleNewCanvas}
          >
            Clear Canvas
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="drawing-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/drawing</h1>}
        rightActions={rightActions}
      />
      <div className="drawing-toolbar">
        <div className="drawing-tool-group">
          <button
            className={`drawing-tool-btn ${tool === 'brush' ? 'active' : ''}`}
            onClick={() => setTool('brush')}
            title="Brush"
          >
            Brush
          </button>
          <button
            className={`drawing-tool-btn ${tool === 'eraser' ? 'active' : ''}`}
            onClick={() => setTool('eraser')}
            title="Eraser"
          >
            Eraser
          </button>
        </div>
        <div className="drawing-size-controls">
          {tool === 'brush' ? (
            <div className="drawing-size-control">
              <label className="drawing-size-label">Brush Size: {brushSize}</label>
              <input
                type="range"
                min="1"
                max="50"
                value={brushSize}
                onChange={(e) => setBrushSize(parseInt(e.target.value))}
                className="drawing-size-slider"
              />
            </div>
          ) : (
            <div className="drawing-size-control">
              <label className="drawing-size-label">Eraser Size: {eraserSize}</label>
              <input
                type="range"
                min="1"
                max="50"
                value={eraserSize}
                onChange={(e) => setEraserSize(parseInt(e.target.value))}
                className="drawing-size-slider"
              />
            </div>
          )}
          {backgroundImage && (
            <>
              <div className="drawing-opacity-control">
                <button
                  className={`drawing-background-toggle ${showBackground ? 'active' : ''}`}
                  onClick={handleToggleBackground}
                  title={showBackground ? 'Hide Background' : 'Show Background'}
                >
                  {showBackground ? '👁️' : '👁️‍🗨️'}
                </button>
                <label className="drawing-size-label">Opacity: {Math.round(backgroundOpacity * 100)}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={backgroundOpacity * 100}
                  onChange={(e) => handleBackgroundOpacityChange(parseInt(e.target.value) / 100)}
                  className="drawing-size-slider"
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="drawing-canvas-wrapper">
        {isLoading && (
          <div className="drawing-loading-overlay">
            <div className="drawing-loading-text">Loading...</div>
          </div>
        )}
        {backgroundImage && showBackground && (
          <img
            src={backgroundImage}
            alt="Background sketch"
            className="drawing-background-image"
            style={{ opacity: backgroundOpacity }}
          />
        )}
        <canvas
          ref={canvasRef}
          className="drawing-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
      </div>
      {showGenerateModal && (
        <div className="drawing-generate-modal" onClick={() => setShowGenerateModal(false)}>
          <div className="drawing-generate-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="drawing-generate-modal-title">Generate Sketch</h2>
            <p className="drawing-generate-modal-description">
              Describe what you'd like to draw. The AI will create a simple sketch for you to trace.
            </p>
            <textarea
              className="drawing-generate-prompt"
              value={generatePrompt}
              onChange={(e) => setGeneratePrompt(e.target.value)}
              placeholder="e.g., a simple cat, a flower, a house"
              rows={4}
              disabled={isGenerating}
            />
            <div className="drawing-generate-modal-actions">
              <button
                className="drawing-generate-btn"
                onClick={handleConfirmGenerate}
                disabled={isGenerating || !generatePrompt.trim()}
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
              <button
                className="drawing-generate-cancel"
                onClick={() => {
                  setShowGenerateModal(false);
                  setGeneratePrompt('');
                }}
                disabled={isGenerating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showPastDrawings && (
        <PastDrawings
          user={user}
          onLoadDrawing={handleLoadDrawing}
          onClose={() => setShowPastDrawings(false)}
        />
      )}
    </div>
  );
};

export default Drawing;
